import type { ComputedRef } from 'vue';

import { computed, ref } from 'vue';

// Tells a user's pan/zoom apart from the camera moves the app makes itself.
export interface CameraGuard {
	isGesture: ComputedRef<boolean>;
	generation(): number;
	isCurrent(generation: number): boolean;
	markProgrammatic(): void;
	beginGesture(onGesture: () => void): void;
	reset(): void;
}

// Lab: each move bumps a generation so late, stale viewport writes are dropped.
export function createTrackedCameraGuard(): CameraGuard {
	let current = 0;
	const intent = ref<'gesture' | 'programmatic'>('programmatic');
	const move = (next: 'gesture' | 'programmatic') => {
		current++;
		intent.value = next;
	};
	return {
		isGesture: computed(() => intent.value === 'gesture'),
		generation: () => current,
		isCurrent: generation => generation === current,
		markProgrammatic: () => move('programmatic'),
		beginGesture(onGesture) {
			move('gesture');
			onGesture();
		},
		reset: () => move('programmatic')
	};
}

// Production never tracked camera intent; every viewport write stays current.
export function createUntrackedCameraGuard(): CameraGuard {
	return {
		isGesture: computed(() => false),
		generation: () => 0,
		isCurrent: () => true,
		markProgrammatic: () => {},
		beginGesture: () => {},
		reset: () => {}
	};
}
